import { useState } from 'react';
import { Button, Card, Chip, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  TIMELINE_EVENT_TYPES,
  useTimeline,
  type TimelineEvent,
  type TimelineEventType,
} from '../../hooks/useUserDetail';
import { ContentModal } from './ContentModal';
import type { ContentEventType } from '../../hooks/useContent';

const CONTENT_TYPES: ContentEventType[] = ['reflection', 'check_in', 'chat_message'];

const ICONS: Record<TimelineEventType, string> = {
  check_in: '◆',
  reflection: '✎',
  chat_message: '❝',
  expansion_completed: '↗',
  practice_completed: '✓',
  synthesis_run: '⟳',
  subscription_event: '$',
  milestone: '★',
};

/** Groups by the MENTEE's local day, not the viewer's — a journey reads in the
 *  timezone it was lived in. */
function localDay(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function Timeline({ userId, timezone }: { userId: string; timezone: string }) {
  const [types, setTypes] = useState<TimelineEventType[]>([]);
  // Mantine 9's DatePickerInput emits DatesRangeValue<string> — a
  // [string | null, string | null] of 'YYYY-MM-DD', NOT Date objects. Holding Dates
  // here (as the plan did) is a type error, and coercing at the boundary instead
  // would reintroduce the local-vs-UTC ambiguity this screen is careful about.
  const [range, setRange] = useState<[string | null, string | null]>([null, null]);
  const [openEvent, setOpenEvent] = useState<TimelineEvent | null>(null);

  const query = useTimeline(userId, {
    types,
    from: range[0] ? new Date(range[0]).toISOString() : null,
    to: range[1] ? new Date(range[1]).toISOString() : null,
  });

  const events = query.data?.pages.flatMap((p) => p.events) ?? [];
  const days = new Map<string, TimelineEvent[]>();
  for (const event of events) {
    const day = localDay(event.occurred_at, timezone);
    const bucket = days.get(day);
    if (bucket) bucket.push(event);
    else days.set(day, [event]);
  }

  return (
    <Stack>
      <Group>
        <Chip.Group multiple value={types} onChange={(v) => setTypes(v as TimelineEventType[])}>
          <Group gap={4}>
            {TIMELINE_EVENT_TYPES.map((t) => (
              <Chip key={t} value={t} size="xs">
                {t.replace(/_/g, ' ')}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        <DatePickerInput
          type="range"
          size="xs"
          placeholder="Date range"
          value={range}
          onChange={setRange}
          clearable
          w={230}
        />
      </Group>

      {!query.isFetching && events.length === 0 && (
        <Text c="dimmed" size="sm">
          No events in this range.
        </Text>
      )}

      {[...days.entries()].map(([day, dayEvents]) => (
        <Stack key={day} gap={4}>
          <Title order={6} data-testid="day-heading">
            {day}
          </Title>
          {dayEvents.map((event) => (
            <Card key={`${event.type}:${event.resource_id}`} withBorder padding="xs">
              <Group gap="xs" wrap="nowrap">
                <Text size="lg" w={20} ta="center">
                  {ICONS[event.type]}
                </Text>
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {new Date(event.occurred_at).toLocaleTimeString()} ·{' '}
                    {event.type.replace(/_/g, ' ')}
                  </Text>
                  <Text size="sm">{event.summary}</Text>
                </Stack>
              </Group>
              {/* Only these three types carry readable content. Every other event
                  is structural and has nothing behind it to gate. */}
              {CONTENT_TYPES.includes(event.type as ContentEventType) && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  mt={4}
                  onClick={() => setOpenEvent(event)}
                >
                  Show content
                </Button>
              )}
            </Card>
          ))}
        </Stack>
      ))}

      {query.isFetching && <Loader size="sm" />}
      {query.hasNextPage && (
        <Button variant="light" onClick={() => void query.fetchNextPage()}>
          Load more
        </Button>
      )}

      {/* Keyed on the event so reopening a different card mounts a fresh modal
          rather than reusing the previous one's committed-reason state. */}
      {openEvent && (
        <ContentModal
          key={`${openEvent.type}:${openEvent.resource_id}`}
          opened
          onClose={() => setOpenEvent(null)}
          userId={userId}
          eventType={openEvent.type as ContentEventType}
          resourceId={openEvent.resource_id}
          // A chat row's resource_id is the message id; the transcript read is
          // keyed by conversation, so pass the row's conversation_id through.
          conversationId={openEvent.conversation_id ?? undefined}
        />
      )}
    </Stack>
  );
}
